/**
 * FachbereicheContext
 * 
 * Provides Fachbereiche (departments) fetched from the backend API.
 * Automatically refreshes when Fachbereiche are added or updated.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useAuth } from './AuthContext';

interface FachbereicheContextType {
  fachbereiche: string[];
  loading: boolean;
  error: string | null;
  refreshFachbereiche: () => Promise<void>;
}

const FachbereicheContext = createContext<FachbereicheContextType | undefined>(undefined);

export const FachbereicheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fachbereiche, setFachbereiche] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token, loading: authLoading } = useAuth();

  const fetchFachbereiche = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!token) {
      setLoading(false);
      setFachbereiche([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/api/fachbereiche`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const names = response.data.fachbereiche.map((fb: any) => fb.name).sort();
        setFachbereiche(names);
      } else {
        setError(response.data.message || 'Fehler beim Laden der Fachbereiche');
        // Fallback to default list if API fails
        setFachbereiche([
          'Bewirtung',
          'Helfermanagement',
          'Kuchenverkauf',
          'Laufstrecke',
          'Rad Fitness',
          'Rad Römerman',
          'Römerkids',
          'Schwimmen Fitness',
          'Schwimmen Römerman',
          'Startnummern',
          'Wechselzone',
          'Zielverpflegung'
        ]);
      }
    } catch (err: any) {
      // Only log error if it's not a 401 (unauthorized) - that's expected when not logged in
      if (err.response?.status !== 401) {
        console.error('Error fetching Fachbereiche:', err);
      }
      setError(err.response?.data?.message || 'Fehler beim Laden der Fachbereiche');
      // Fallback to default list if API fails
      setFachbereiche([
        'Bewirtung',
        'Helfermanagement',
        'Kuchenverkauf',
        'Laufstrecke',
        'Rad Fitness',
        'Rad Römerman',
        'Römerkids',
        'Schwimmen Fitness',
        'Schwimmen Römerman',
        'Startnummern',
        'Wechselzone',
        'Zielverpflegung'
      ]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Wait for auth to finish loading before fetching Fachbereiche
    if (!authLoading) {
      fetchFachbereiche();
    }
  }, [fetchFachbereiche, authLoading]);

  return (
    <FachbereicheContext.Provider value={{ fachbereiche, loading, error, refreshFachbereiche: fetchFachbereiche }}>
      {children}
    </FachbereicheContext.Provider>
  );
};

export const useFachbereiche = () => {
  const context = useContext(FachbereicheContext);
  if (context === undefined) {
    throw new Error('useFachbereiche must be used within a FachbereicheProvider');
  }
  return context;
};

